import { useState, useEffect, useRef, useCallback } from 'react';
import { Zap, Play, Clock, Trophy, Target, Shield, AlertTriangle, CheckCircle, XCircle, ChevronRight, Award, Timer, RotateCcw } from 'lucide-react';
import { axios } from '../App';
import { toast } from 'sonner';

const CLASSIFICATIONS = [
  { id: 'consumer', label: 'Consumer' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'recon', label: 'Reconnaissance' },
  { id: 'military', label: 'Military' },
  { id: 'weaponized', label: 'Weaponized' },
];

const SOFT_KILLS = [
  { id: 'rf_jamming', label: 'RF Jamming' },
  { id: 'gps_spoofing', label: 'GPS Spoofing' },
  { id: 'comm_disruption', label: 'Comm Disruption' },
  { id: 'cyber_takeover', label: 'Cyber Takeover' },
];

const HARD_KILLS = [
  { id: 'directed_energy', label: 'Directed Energy' },
  { id: 'kinetic_interceptor', label: 'Kinetic Interceptor' },
  { id: 'net_capture', label: 'Net Capture' },
  { id: 'anti_drone_missile', label: 'Anti-Drone Missile' },
];

const GRADE_STYLES = {
  S: 'text-yellow-300 bg-yellow-500/20 border-yellow-500',
  A: 'text-green-400 bg-green-500/20 border-green-500',
  B: 'text-sky-400 bg-sky-500/20 border-sky-500',
  C: 'text-orange-400 bg-orange-500/20 border-orange-500',
  F: 'text-red-400 bg-red-500/20 border-red-500',
};

function formatTime(s) {
  if (s == null) return '--';
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${(s % 60).toFixed(0)}s`;
}

function LiveTimer({ startTime, running }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!running || !startTime) return;
    const interval = setInterval(() => {
      setElapsed((Date.now() - new Date(startTime).getTime()) / 1000);
    }, 100);
    return () => clearInterval(interval);
  }, [running, startTime]);
  return (
    <span className={`text-3xl font-bold font-mono tabular-nums ${elapsed > 20 ? 'text-red-400' : elapsed > 10 ? 'text-yellow-400' : 'text-green-400'}`}
      data-testid="live-timer">
      {elapsed.toFixed(1)}s
    </span>
  );
}

export default function DrillMode() {
  const [phase, setPhase] = useState('idle'); // idle, active, result
  const [drill, setDrill] = useState(null);
  const [response, setResponse] = useState({ classification: '', soft_kill: '', hard_kill: '' });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingLb, setLoadingLb] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');

  useEffect(() => {
    fetchLeaderboard();
    fetchHistory();
  }, []);

  const fetchLeaderboard = async () => {
    setLoadingLb(true);
    try {
      const res = await axios.get('/drill/leaderboard');
      setLeaderboard(res.data);
    } catch { /* silent */ }
    finally { setLoadingLb(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get('/drill/history');
      setHistory(res.data);
    } catch { /* silent */ }
  };

  const startDrill = async () => {
    setStarting(true);
    setResult(null);
    setResponse({ classification: '', soft_kill: '', hard_kill: '' });
    try {
      const res = await axios.post('/drill/start');
      setDrill(res.data);
      setPhase('active');
    } catch (err) {
      toast.error('Failed to start drill');
    } finally { setStarting(false); }
  };

  const submitResponse = async () => {
    if (!response.classification || !response.soft_kill || !response.hard_kill) {
      toast.error('Select all three responses before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`/drill/${drill.id}/respond`, response);
      setResult(res.data);
      setPhase('result');
      fetchLeaderboard();
      fetchHistory();
    } catch (err) {
      toast.error('Submit failed');
    } finally { setSubmitting(false); }
  };

  const threatColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      default: return 'text-green-500 border-green-500 bg-green-500/10';
    }
  };

  const myStats = history.length > 0 ? {
    total: history.length,
    avgScore: (history.reduce((s, h) => s + (h.score || 0), 0) / history.length).toFixed(1),
    avgTime: (history.reduce((s, h) => s + (h.response_time_seconds || 0), 0) / history.length).toFixed(1),
    bestScore: Math.max(...history.map(h => h.score || 0)),
  } : null;

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="drill-mode-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Zap size={24} className="text-amber-400" />
              Drill Mode
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              Rapid-Fire Kill Chain Training | Randomized Threats | Team Leaderboard
            </p>
          </div>
          <div className="flex items-center gap-4">
            {myStats && (
              <div className="flex items-center gap-4 text-center">
                <div>
                  <div className="text-lg font-bold font-mono text-amber-400" data-testid="my-total">{myStats.total}</div>
                  <div className="text-[10px] text-zinc-500 uppercase">Drills</div>
                </div>
                <div>
                  <div className="text-lg font-bold font-mono text-green-400" data-testid="my-avg-score">{myStats.avgScore}</div>
                  <div className="text-[10px] text-zinc-500 uppercase">Avg Score</div>
                </div>
                <div>
                  <div className="text-lg font-bold font-mono text-sky-400" data-testid="my-avg-time">{myStats.avgTime}s</div>
                  <div className="text-[10px] text-zinc-500 uppercase">Avg Time</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Drill Area */}
        <div className="w-[60%] border-r border-zinc-800 flex flex-col overflow-hidden">
          {/* Idle State */}
          {phase === 'idle' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 rounded-full bg-amber-500/10 border-2 border-amber-500/30 flex items-center justify-center mx-auto mb-6">
                  <Zap size={36} className="text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Ready for Training?</h2>
                <p className="text-sm text-zinc-400 mb-6">
                  A randomized drone threat will appear. Classify it, select countermeasures, and submit as fast as you can. Score is based on accuracy + speed.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-6 text-xs">
                  <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
                    <Target size={20} className="text-purple-400 mx-auto mb-1" />
                    <div className="text-zinc-300 font-bold">Classify</div>
                    <div className="text-zinc-500">40 pts</div>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
                    <Shield size={20} className="text-yellow-400 mx-auto mb-1" />
                    <div className="text-zinc-300 font-bold">Soft Kill</div>
                    <div className="text-zinc-500">30 pts</div>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
                    <Zap size={20} className="text-red-400 mx-auto mb-1" />
                    <div className="text-zinc-300 font-bold">Hard Kill</div>
                    <div className="text-zinc-500">30 pts</div>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Speed bonus: +10 pts (under 10s) | +5 pts (under 20s)</p>
                <button onClick={startDrill} disabled={starting}
                  className="px-8 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-800 rounded-lg font-mono text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2 mx-auto transition-colors text-black"
                  data-testid="start-drill-btn">
                  {starting ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <Play size={18} />}
                  {starting ? 'Loading...' : 'Start Drill'}
                </button>
              </div>
            </div>
          )}

          {/* Active Drill */}
          {phase === 'active' && drill && (
            <div className="flex-1 flex flex-col overflow-y-auto">
              {/* Timer Bar */}
              <div className="p-4 border-b border-zinc-800 bg-zinc-900/80 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-xs font-mono uppercase text-red-400 tracking-wider">Live Drill</span>
                </div>
                <LiveTimer startTime={drill.started_at} running={true} />
              </div>

              {/* Threat Briefing */}
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-400 flex items-center gap-2">
                    <AlertTriangle size={16} /> Incoming Threat
                  </h3>
                  <span className={`px-3 py-1 rounded border text-xs font-mono uppercase font-bold ${threatColor(drill.scenario.threat_level)}`}
                    data-testid="drill-threat-level">
                    {drill.scenario.threat_level}
                  </span>
                </div>
                <div className="bg-zinc-900 border border-red-500/30 rounded p-4" data-testid="threat-briefing">
                  <p className="text-sm text-zinc-200 mb-3">{drill.scenario.description}</p>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><span className="text-zinc-500">Domain:</span> <span className="text-zinc-200 font-mono uppercase">{drill.scenario.domain}</span></div>
                    <div><span className="text-zinc-500">Speed:</span> <span className="text-zinc-200 font-mono">{drill.scenario.speed} km/h</span></div>
                    <div><span className="text-zinc-500">Altitude:</span> <span className="text-zinc-200 font-mono">{drill.scenario.altitude} m</span></div>
                    <div><span className="text-zinc-500">Distance:</span> <span className="text-zinc-200 font-mono">{drill.scenario.distance} km</span></div>
                    <div><span className="text-zinc-500">Payload:</span> <span className="text-zinc-200 font-mono">{drill.scenario.payload}</span></div>
                    <div><span className="text-zinc-500">Category:</span> <span className="text-zinc-200 font-mono">{drill.scenario.category}</span></div>
                  </div>
                </div>
              </div>

              {/* Response Selection */}
              <div className="p-4 space-y-4 flex-1">
                {/* Classification */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-400 mb-2 flex items-center gap-2">
                    <Target size={14} /> 1. Classify Threat (40 pts)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {CLASSIFICATIONS.map(c => (
                      <button key={c.id} onClick={() => setResponse(p => ({ ...p, classification: c.id }))}
                        className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-all ${
                          response.classification === c.id
                            ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                            : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`} data-testid={`class-${c.id}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Soft Kill */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-yellow-400 mb-2 flex items-center gap-2">
                    <Shield size={14} /> 2. Select Soft Kill (30 pts)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {SOFT_KILLS.map(s => (
                      <button key={s.id} onClick={() => setResponse(p => ({ ...p, soft_kill: s.id }))}
                        className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-all ${
                          response.soft_kill === s.id
                            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300'
                            : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`} data-testid={`soft-${s.id}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hard Kill */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-red-400 mb-2 flex items-center gap-2">
                    <Zap size={14} /> 3. Authorize Hard Kill (30 pts)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {HARD_KILLS.map(h => (
                      <button key={h.id} onClick={() => setResponse(p => ({ ...p, hard_kill: h.id }))}
                        className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-all ${
                          response.hard_kill === h.id
                            ? 'bg-red-500/20 border-red-500 text-red-300'
                            : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`} data-testid={`hard-${h.id}`}>
                        {h.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={submitResponse} disabled={submitting || !response.classification || !response.soft_kill || !response.hard_kill}
                  className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-zinc-700 disabled:text-zinc-400 rounded font-mono text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2 transition-colors text-black disabled:text-zinc-400"
                  data-testid="submit-response-btn">
                  {submitting ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <ChevronRight size={18} />}
                  {submitting ? 'Evaluating...' : 'Submit Response'}
                </button>
              </div>
            </div>
          )}

          {/* Result */}
          {phase === 'result' && result && (
            <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
              <div className="max-w-lg w-full space-y-4">
                {/* Grade */}
                <div className="text-center mb-2">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 text-4xl font-black font-mono ${GRADE_STYLES[result.grade] || GRADE_STYLES.F}`}
                    data-testid="drill-grade">
                    {result.grade}
                  </div>
                  <div className="mt-3">
                    <span className="text-3xl font-bold font-mono text-white" data-testid="drill-score">{result.score}</span>
                    <span className="text-zinc-500 text-lg">/100</span>
                  </div>
                  <div className="text-sm text-zinc-400 mt-1">
                    Response Time: <span className="text-sky-400 font-mono">{formatTime(result.response_time_seconds)}</span>
                  </div>
                </div>

                {/* Feedback */}
                <div className="bg-zinc-900 border border-zinc-800 rounded p-4 space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Feedback</h4>
                  {(result.feedback || []).map((f, i) => {
                    const isCorrect = f.includes('CORRECT');
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {isCorrect ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-500" />}
                        <span className={isCorrect ? 'text-green-300' : 'text-red-300'}>{f}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Scenario Recap */}
                <div className="bg-zinc-900 border border-zinc-800 rounded p-4 text-xs">
                  <h4 className="font-bold uppercase tracking-wider text-zinc-400 mb-2">Scenario</h4>
                  <p className="text-zinc-300 mb-2">{result.scenario?.description}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-zinc-500">Target:</span>
                    <span className="text-zinc-200 font-mono">{result.scenario?.drone_type}</span>
                    <span className={`px-2 py-0.5 rounded border text-[10px] font-mono uppercase ${threatColor(result.scenario?.threat_level)}`}>
                      {result.scenario?.threat_level}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={startDrill}
                    className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 rounded font-mono text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2 transition-colors text-black"
                    data-testid="next-drill-btn">
                    <Zap size={16} /> Next Drill
                  </button>
                  <button onClick={() => { setPhase('idle'); setDrill(null); setResult(null); }}
                    className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 rounded font-mono text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors text-zinc-300"
                    data-testid="back-btn">
                    <RotateCcw size={16} /> Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Leaderboard & History */}
        <div className="w-[40%] flex flex-col overflow-hidden">
          <div className="p-4 border-b border-zinc-800 flex gap-2">
            <button onClick={() => setActiveTab('leaderboard')}
              className={`flex-1 py-2 text-xs font-mono uppercase rounded transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === 'leaderboard' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
              }`} data-testid="tab-leaderboard">
              <Trophy size={14} /> Leaderboard
            </button>
            <button onClick={() => setActiveTab('history')}
              className={`flex-1 py-2 text-xs font-mono uppercase rounded transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === 'history' ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
              }`} data-testid="tab-history">
              <Clock size={14} /> My History
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'leaderboard' && (
              <>
                {loadingLb ? (
                  <div className="text-center py-8 text-zinc-500 text-sm">Loading leaderboard...</div>
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Trophy size={36} className="mx-auto mb-2 opacity-20" />
                    <p className="text-sm">No entries yet. Be the first!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {leaderboard.map((entry, i) => (
                      <div key={i} className={`rounded p-3 border transition-all ${
                        i === 0 ? 'bg-amber-500/5 border-amber-500/30' :
                        i === 1 ? 'bg-zinc-500/5 border-zinc-400/20' :
                        i === 2 ? 'bg-orange-500/5 border-orange-700/20' :
                        'bg-zinc-900 border-zinc-800'
                      }`} data-testid={`lb-entry-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`text-lg font-black font-mono ${
                              i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-orange-400' : 'text-zinc-500'
                            }`}>#{i + 1}</span>
                            <div>
                              <div className="text-sm font-bold text-white">{entry.operator || 'Unknown'}</div>
                              <div className="text-[10px] text-zinc-500">{entry.drills_completed} drills completed</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold font-mono text-amber-400">{entry.avg_score}</div>
                            <div className="text-[10px] text-zinc-500">avg score</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-[10px]">
                          <div className="text-center bg-zinc-800/50 rounded py-1">
                            <div className="font-mono text-green-400">{entry.best_score}</div>
                            <div className="text-zinc-500">Best</div>
                          </div>
                          <div className="text-center bg-zinc-800/50 rounded py-1">
                            <div className="font-mono text-sky-400">{entry.fastest_response}s</div>
                            <div className="text-zinc-500">Fastest</div>
                          </div>
                          <div className="text-center bg-zinc-800/50 rounded py-1">
                            <div className="font-mono text-yellow-300">{entry.total_s_grades}</div>
                            <div className="text-zinc-500">S Rank</div>
                          </div>
                          <div className="text-center bg-zinc-800/50 rounded py-1">
                            <div className="font-mono text-green-400">{entry.total_a_grades}</div>
                            <div className="text-zinc-500">A Rank</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'history' && (
              <>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Clock size={36} className="mx-auto mb-2 opacity-20" />
                    <p className="text-sm">No drill history yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {history.map(h => (
                      <div key={h.id} className="bg-zinc-900 border border-zinc-800 rounded p-3" data-testid={`history-${h.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded flex items-center justify-center border text-sm font-black font-mono ${GRADE_STYLES[h.grade] || GRADE_STYLES.F}`}>
                              {h.grade}
                            </span>
                            <div>
                              <div className="text-xs font-bold text-white">{h.scenario?.drone_type || 'Unknown'}</div>
                              <div className="text-[10px] text-zinc-500">{h.scenario?.threat_level} | {h.scenario?.domain}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold font-mono text-white">{h.score}<span className="text-zinc-500 text-xs">/100</span></div>
                            <div className="text-[10px] text-zinc-400 font-mono">{formatTime(h.response_time_seconds)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
